import { Component, OnDestroy, AfterViewInit, ViewContainerRef, TemplateRef, inject, input, viewChild } from '@angular/core';
import { Overlay, OverlayRef, ConnectedPosition } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

const POSITION_MAP: Record<string, ConnectedPosition[]> = {
  top: [
    { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8, panelClass: 'tooltip-top' },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8,  panelClass: 'tooltip-bottom' },
  ],
  bottom: [
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8,  panelClass: 'tooltip-bottom' },
    { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8, panelClass: 'tooltip-top' },
  ],
  left: [
    { originX: 'start',  originY: 'center', overlayX: 'end',    overlayY: 'center', offsetX: -8, panelClass: 'tooltip-left' },
    { originX: 'end',    originY: 'center', overlayX: 'start',  overlayY: 'center', offsetX: 8,  panelClass: 'tooltip-right' },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8,  panelClass: 'tooltip-bottom' },
    { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8, panelClass: 'tooltip-top' },
  ],
  right: [
    { originX: 'end',    originY: 'center', overlayX: 'start',  overlayY: 'center', offsetX: 8,  panelClass: 'tooltip-right' },
    { originX: 'start',  originY: 'center', overlayX: 'end',    overlayY: 'center', offsetX: -8, panelClass: 'tooltip-left' },
    { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8,  panelClass: 'tooltip-bottom' },
    { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8, panelClass: 'tooltip-top' },
  ],
};

const DEFAULT_POSITIONS: ConnectedPosition[] = [
  { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top',    offsetY: 8,  panelClass: 'tooltip-bottom' },
  { originX: 'center', originY: 'top',    overlayX: 'center', overlayY: 'bottom', offsetY: -8, panelClass: 'tooltip-top' },
  { originX: 'end',    originY: 'center', overlayX: 'start',  overlayY: 'center', offsetX: 8,  panelClass: 'tooltip-right' },
  { originX: 'start',  originY: 'center', overlayX: 'end',    overlayY: 'center', offsetX: -8, panelClass: 'tooltip-left' },
];

@Component({
  selector: 'app-tooltip',
  standalone: true,
  imports: [],
  templateUrl: './tooltip.component.html',
  styleUrl: './tooltip.component.css'
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
  triggerEl = input.required<HTMLElement>();
  placement = input<string>('');
  triggerType = input<string>('hover');
  tooltipTemplate = viewChild.required('tooltipTemplate', { read: TemplateRef });

  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;
  private listeners: (() => void)[] = [];

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  ngAfterViewInit() {
    const positions = POSITION_MAP[this.placement()] ?? DEFAULT_POSITIONS;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.triggerEl())
      .withPositions(positions)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.portal = new TemplatePortal(this.tooltipTemplate(), this.viewContainerRef);

    this.attachListeners();
  }

  private attachListeners() {
    const triggerEl = this.triggerEl();

    if (this.triggerType() === 'click') {
      const onClick = () => this.isOpen() ? this.hide() : this.show();
      triggerEl.addEventListener('click', onClick);
      this.listeners.push(() => triggerEl.removeEventListener('click', onClick));

      const onOutsideClick = (e: MouseEvent) => {
        if (!triggerEl.contains(e.target as Node)) this.hide();
      };
      document.addEventListener('click', onOutsideClick);
      this.listeners.push(() => document.removeEventListener('click', onOutsideClick));
    } else {
      const onEnter = () => this.show();
      const onLeave = () => this.hide();
      triggerEl.addEventListener('mouseenter', onEnter);
      triggerEl.addEventListener('mouseleave', onLeave);
      this.listeners.push(() => triggerEl.removeEventListener('mouseenter', onEnter));
      this.listeners.push(() => triggerEl.removeEventListener('mouseleave', onLeave));
    }
  }

  show() {
    if (this.overlayRef && this.portal && !this.overlayRef.hasAttached()) {
      this.overlayRef.attach(this.portal);
    }
  }

  hide() {
    this.overlayRef?.detach();
  }

  isOpen() {
    return this.overlayRef?.hasAttached() ?? false;
  }

  ngOnDestroy() {
    this.listeners.forEach(fn => fn());
    this.overlayRef?.dispose();
  }
}
